import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  brokerStatsById,
  motorsByBroker,
  policiesByBroker,
  propertiesByBroker,
} from "../portfolioData";
import { formatDate, formatMoney } from "../helpers";
import FieldList from "./FieldList";
import BrokerChart from "./BrokerChart";
import EntityCard from "./EntityCard";
import { LAYERS } from "../constants";

function BrokerDetail({ broker, language, onOpen }) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState("month");
  const [assetTab, setAssetTab] = useState(LAYERS.property);
  const [policiesOpen, setPoliciesOpen] = useState(false);

  useEffect(() => {
    setAssetTab(LAYERS.property);
    setPoliciesOpen(false);
  }, [broker.id]);
  const stats = brokerStatsById[broker.id];
  const brokerPolicies = policiesByBroker[broker.id] || [];

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2 }}>
        <Box
          component="img"
          src={broker.logo_url}
          alt=""
          sx={{ width: 48, height: 48, borderRadius: "50%" }}
        />
        <Typography variant="h6">{broker.name}</Typography>
      </Box>

      <FieldList
        rows={[
          { label: t("portfolioExplorer.fields.address"), value: broker.address },
          { label: t("portfolioExplorer.fields.postalCode"), value: broker.postal_code },
          { label: t("portfolioExplorer.fields.city"), value: broker.city },
          { label: t("portfolioExplorer.fields.region"), value: broker.region },
          { label: t("portfolioExplorer.fields.country"), value: broker.country },
          { label: t("portfolioExplorer.fields.email"), value: broker.email },
          { label: t("portfolioExplorer.fields.phone"), value: broker.phone },
          { label: t("portfolioExplorer.fields.manager"), value: broker.manager_name },
          { label: t("portfolioExplorer.fields.addedDate"), value: formatDate(broker.added_date, language) },
        ]}
      />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("portfolioExplorer.stats.title")}
      </Typography>
      <FieldList
        rows={[
          { label: t("portfolioExplorer.stats.properties"), value: stats.propertyCount },
          { label: t("portfolioExplorer.stats.motors"), value: stats.motorCount },
          { label: t("portfolioExplorer.stats.propertyInsured"), value: formatMoney(stats.propertyInsured, language) },
          { label: t("portfolioExplorer.stats.contentsInsured"), value: formatMoney(stats.contentsInsured, language) },
          { label: t("portfolioExplorer.stats.motorInsured"), value: formatMoney(stats.motorInsured, language) },
          { label: t("portfolioExplorer.fields.premium"), value: formatMoney(stats.totalPremium, language) },
          { label: t("portfolioExplorer.stats.propertyClaims"), value: stats.propertyClaimCount },
          { label: t("portfolioExplorer.stats.motorClaims"), value: stats.motorClaimCount },
          { label: t("portfolioExplorer.stats.claimedAmount"), value: formatMoney(stats.claimedAmount, language) },
          { label: t("portfolioExplorer.stats.paidAmount"), value: formatMoney(stats.paidAmount, language) },
        ]}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1">{t("portfolioExplorer.chart.title")}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={granularity}
          onChange={(_, value) => value && setGranularity(value)}
        >
          <ToggleButton value="day">{t("portfolioExplorer.chart.day")}</ToggleButton>
          <ToggleButton value="month">{t("portfolioExplorer.chart.month")}</ToggleButton>
          <ToggleButton value="year">{t("portfolioExplorer.chart.year")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <BrokerChart brokerId={broker.id} granularity={granularity} />

      <Tabs
        value={assetTab}
        onChange={(_, value) => setAssetTab(value)}
        sx={{ mb: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value={LAYERS.property} label={t("portfolioExplorer.connectedProperties")} />
        <Tab value={LAYERS.motor} label={t("portfolioExplorer.connectedMotors")} />
      </Tabs>
      {assetTab === LAYERS.property
        ? (propertiesByBroker[broker.id] || []).map((property) => (
            <EntityCard
              key={property.id}
              entity={property}
              layer={LAYERS.property}
              language={language}
              onOpen={onOpen}
            />
          ))
        : (motorsByBroker[broker.id] || []).map((motor) => (
            <EntityCard
              key={motor.id}
              entity={motor}
              layer={LAYERS.motor}
              language={language}
              onOpen={onOpen}
            />
          ))}

      <Accordion
        expanded={policiesOpen}
        onChange={(_, expanded) => setPoliciesOpen(expanded)}
        disableGutters
        elevation={0}
        sx={{ mt: 2, bgcolor: "transparent", "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="subtitle1">{t("portfolioExplorer.policies")}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          {brokerPolicies.map((policy) => (
            <Box key={policy.id} sx={{ mb: 1.5, p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {policy.policy_number}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {t(`portfolioExplorer.enums.lineOfBusiness.${policy.line_of_business}`)} · {t(`portfolioExplorer.enums.status.${policy.status}`)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {formatDate(policy.start_date, language)} – {formatDate(policy.end_date, language)} · {formatMoney(policy.gross_premium, language)}
              </Typography>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default BrokerDetail;
